// src/Profile.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "./firebase"; 
import { onAuthStateChanged } from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";
import { getUserProfile, getUserTracks, getLikedTracks, getFavoriteTracks } from "./database";
import Header from "./Header";

// Spinner animasyonu için style tag ekle
const spinnerStyle = document.createElement("style");
spinnerStyle.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
if (!document.head.querySelector('style[data-spinner]')) {
  spinnerStyle.setAttribute('data-spinner', 'true');
  document.head.appendChild(spinnerStyle);
}

function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  
  const [userProfile, setUserProfile] = useState(null);
  const [userTracks, setUserTracks] = useState([]);
  const [likedTracks, setLikedTracks] = useState([]);
  const [favoriteTracks, setFavoriteTracks] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("my-tracks");

  const [showEditModal, setShowEditModal] = useState(false);
  const [tempBio, setTempBio] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [selectedTrackForPreview, setSelectedTrackForPreview] = useState(null);

  // 1. Auth state'i kontrol et
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthChecked(true);
      
      if (!currentUser) {
        setLoading(false);
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // 2. Kullanıcı verilerini yükle
  useEffect(() => {
    const loadUserData = async () => {
      if (!user) return;
      
      setLoading(true);
      try {
        const profile = await getUserProfile(user.uid);
        setUserProfile(profile);
        
        if (profile?.bio) setTempBio(profile.bio);

        const tracks = await getUserTracks(user.uid);
        setUserTracks(tracks);

        const liked = await getLikedTracks(user.uid);
        setLikedTracks(liked);

        const favorites = await getFavoriteTracks(user.uid);
        setFavoriteTracks(favorites); 

      } catch (error) {
        console.error("❌ Profil yükleme hatası:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadUserData();
  }, [user]);

  const handleEditClick = () => {
    setTempBio(userProfile?.bio || "");
    setShowEditModal(true);
  };

  const handleSaveBio = async () => {
    if (!user) return;
    setIsSaving(true);
    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        bio: tempBio
      });

      setUserProfile((prev) => ({ ...prev, bio: tempBio }));
      setShowEditModal(false);
    } catch (error) {
      console.error("Bio güncelleme hatası:", error);
      alert("An error occurred, please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Track'e tıklandığında Studio'ya yönlendirme
  const handleTrackClick = (track) => {
    setSelectedTrackForPreview(track);
  };

  const closePreviewModal = () => {
    setSelectedTrackForPreview(null);
  };

  const openInStudio = () => {
  if (!selectedTrackForPreview) return;
  navigate('/studio', { 
    state: { 
      loadedCode: selectedTrackForPreview.code,
      loadedTitle: selectedTrackForPreview.title,
      loadedTrackId: selectedTrackForPreview.id,
      isPublic: selectedTrackForPreview.isPublic || false
    } 
  });
  };

  // Yeni track oluşturma
  const handleCreateNewTrack = () => {
    console.log("✨ Yeni track oluşturuluyor");
    navigate('/studio');
  };

  // Loading veya auth kontrolü devam ediyorsa
  if (!authChecked || loading) {
    return (
      <div style={styles.loadingContainer}>
        <Header user={user} sticky={false} />
        <div style={styles.loadingContent}>
          <div style={styles.loadingSpinner}></div>
          <p style={{color: "#b3b3b3", marginTop: "20px"}}>
            {!authChecked ? "Checking authentication..." : "Loading profile..."}
          </p>
        </div>
      </div>
    );
  }

  // Kullanıcı yoksa (zaten navigate yapılacak ama yine de)
  if (!user) {
    return null;
  }

  // Hesaplamalar
  const displayUsername = userProfile?.username || user?.displayName || user?.email?.split('@')[0] || "User";
  const rank = userProfile?.rank || "-";
  const memberSince = userProfile?.createdAt?.toDate ? 
    Math.floor((Date.now() - userProfile.createdAt.toDate().getTime()) / (1000 * 60 * 60 * 24)) : 0;

  const totalFavorites = userTracks.reduce((sum, track) => sum + (track.favorites || 0), 0);
  const totalPlays = userTracks.reduce((sum, track) => sum + (track.plays || 0), 0);
  const totalTracks = userTracks.length;
  const totalLikes = userProfile?.totalLikes || 0; 

  const xpPerLike = 50;
  const xpPerFav = 100;
  const xpPerTrack = 20;

  const musicXP = (totalLikes * xpPerLike) + (totalFavorites * xpPerFav) + (totalTracks * xpPerTrack);
  const learnXP = userProfile?.exp || 0;  // Database'den gelen Learn XP
  const currentXP = musicXP + learnXP;     // Toplam XP
  
  const xpPerLevel = 1000; 
  const currentLevel = Math.floor(currentXP / xpPerLevel) + 1;
  const xpInCurrentLevel = currentXP % xpPerLevel;
  const progressPercent = (xpInCurrentLevel / xpPerLevel) * 100;

  const userLearningProgress = userProfile?.learningProgress || {
    beginner: Array(12).fill(false),
    intermediate: Array(16).fill(false),
    expert: Array(12).fill(false)
  };

  const beginnerCompleted = userLearningProgress.beginner.filter(Boolean).length;
  const intermediateCompleted = userLearningProgress.intermediate.filter(Boolean).length;
  const expertCompleted = userLearningProgress.expert.filter(Boolean).length;

  const beginnerProgress = Math.round((beginnerCompleted / 12) * 100);
  const intermediateProgress = Math.round((intermediateCompleted / 16) * 100);
  const expertProgress = Math.round((expertCompleted / 12) * 100);

  // Mevcut seviyeyi belirle
  let learningLevel = "Beginner";
  if (beginnerProgress === 100) learningLevel = "Intermediate";
  if (intermediateProgress === 100) learningLevel = "Expert";
  if (expertProgress === 100) learningLevel = "Master";
  const learningProgress = [
    { 
      level: "Beginner", 
      completed: beginnerProgress === 100, 
      lessons: beginnerCompleted, 
      total: 12,
      progress: beginnerProgress 
    },
    { 
      level: "Intermediate", 
      completed: intermediateProgress === 100, 
      lessons: intermediateCompleted, 
      total: 16,
      progress: intermediateProgress 
    },
    { 
      level: "Expert", 
      completed: expertProgress === 100, 
      lessons: expertCompleted, 
      total: 12,
      progress: expertProgress 
    }
  ];

  return (
    <div style={styles.pageWrapper}>
      <Header user={user} />

      <div style={styles.mainContainer}>
        
        <div style={styles.heroSection}>
          <div style={styles.heroContent}>
            
            <div style={styles.heroLeftGroup}>
                <div style={styles.avatarLarge}>
                {displayUsername.charAt(0).toUpperCase()}
                <div style={styles.levelBadge}>LVL {currentLevel}</div>
                </div>
                
                <div style={styles.userInfo}>
                <div style={styles.userHeaderRow}>
                    <h1 style={styles.userName}>@{displayUsername}</h1>
                    <button style={styles.editProfileBtn} onClick={handleEditClick}>
                      ✏️ Edit
                    </button>
                </div>
                
                <div style={styles.rankBadge}>Global Rank: #{rank}</div>
                
                <p style={styles.userBio}>
                    {userProfile?.bio || "Creating new worlds with music and coding..."}
                </p>
                <div style={styles.joinDate}>
                    📅 Member for {memberSince} days
                </div>
                </div>
            </div>

            <div style={styles.xpCard}>
                <div style={styles.xpHeader}>
                    <span style={styles.xpTitle}>LEVEL {currentLevel}</span>
                    <span style={styles.xpPoints}>{currentXP} / {currentLevel * xpPerLevel} XP</span>
                </div>
                
                <div style={styles.xpProgressBarBg}>
                    <div style={{...styles.xpProgressBarFill, width: `${progressPercent}%`}}></div>
                </div>

                <div style={styles.xpBreakdown}>
                    <div style={styles.xpItem}>
                        <span style={{color: '#E91E63'}}>❤️ {totalLikes} Likes</span> 
                        <span style={styles.xpGain}>+{totalLikes * xpPerLike} XP</span>
                    </div>
                    <div style={styles.xpItem}>
                        <span style={{color: '#FFC107'}}>⭐ {totalFavorites} Favorites</span>
                        <span style={styles.xpGain}>+{totalFavorites * xpPerFav} XP</span>
                    </div>
                </div>
                <div style={styles.nextLevelNote}>Need {xpPerLevel - xpInCurrentLevel} XP for next level</div>
            </div>

          </div>
        </div>

        <div style={styles.statsGrid}>
          <div style={styles.statCardHighlight}>
            <div style={styles.statIconFire}>🔥</div>
            <div style={styles.statInfo}>
              <div style={styles.statLabel}>Current Streak</div>
              <div style={styles.statValueHighlight}>{userProfile?.currentStreak || 0} Days</div>
            </div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statValue}>{totalTracks}</div>
            <div style={styles.statLabel}>Total Tracks</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statValue}>{totalPlays}</div>
            <div style={styles.statLabel}>Total Plays</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statValue}>{totalLikes}</div>
            <div style={styles.statLabel}>Likes</div>
          </div>
        </div>


        <div style={styles.sectionContainer}>
          <h2 style={styles.sectionTitle}>Learning Progress</h2>
          <div style={styles.learningPathBar}>
            {learningProgress.map((stage, index) => (
              <div key={stage.level} style={styles.learningStepWrapper}>
                <div style={{
                   ...styles.learningStepCard,
                   borderColor: stage.level === learningLevel ? '#1DB954' : '#333',
                   opacity: stage.level === learningLevel ? 1 : (stage.completed ? 0.8 : 0.4)
                }}>
                  <div style={styles.stepHeader}>
                    <span style={styles.stepTitle}>{stage.level}</span>
                    {stage.completed ? <span style={styles.check}>✓</span> : null}
                  </div>
                  <div style={styles.progressBarBg}>
                    <div style={{...styles.progressBarFill, width: `${stage.progress}%`}}></div>
                  </div>
                  <div style={styles.stepMeta}>{stage.lessons}/{stage.total} Lessons</div>
                </div>
                {index !== learningProgress.length - 1 && <div style={styles.connectorLine} />}
              </div>
            ))}
          </div>
        </div>

        <div style={styles.contentSection}>
          <div style={styles.tabsHeader}>
            <button style={activeTab === 'my-tracks' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('my-tracks')}>
              My Tracks
            </button>
            <button style={activeTab === 'liked' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('liked')}>
              Liked
            </button>
            <button style={activeTab === 'favourites' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('favourites')}>
              Favorites
            </button>
          </div>

          <div style={styles.tabContent}>
            {activeTab === 'my-tracks' && (
              <div style={styles.createNewRow} onClick={handleCreateNewTrack}>
                <div style={styles.plusIcon}>+</div>
                <span>Create New Track</span>
              </div>
            )}

            <div style={styles.trackList}>
              {(activeTab === "my-tracks" ? userTracks : activeTab === "liked" ? likedTracks : favoriteTracks).length === 0 ? (
                <div style={styles.emptyState}>No tracks in this list yet.</div>
              ) : (
                (activeTab === "my-tracks" ? userTracks : activeTab === "liked" ? likedTracks : favoriteTracks).map((track) => (
                  <div key={track.id} style={styles.trackRow} onClick={() => handleTrackClick(track)}>
                    <div style={styles.trackLeft}>
                      <div style={styles.trackIcon}>🎵</div>
                      <div style={styles.trackDetails}>
                        <div style={styles.trackTitle}>{track.title || "Untitled Track"}</div>
                        <div style={styles.trackSubDetail}>
                          {activeTab !== 'my-tracks' && track.ownerUsername ? 
                            <span style={{color: '#1DB954', marginRight: '5px'}}>@{track.ownerUsername} • </span> 
                            : null
                          }
                          {track.createdAt?.toDate ? track.createdAt.toDate().toLocaleDateString('tr-TR') : 'No date'} 
                        </div>
                      </div>
                    </div>
                    <div style={styles.trackRight}>
                      {activeTab === 'my-tracks' && (
                          <span style={track.isPublic ? styles.badgePublic : styles.badgePrivate}>
                            {track.isPublic ? "PUBLIC" : "PRIVATE"}
                          </span>
                      )}
                      <div style={styles.metric}>⭐ {track.favorites || 0}</div> 
                      <div style={styles.metric}>❤️ {track.likes || 0}</div>
                      <div style={styles.metric}>▶️ {track.plays || 0}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

      </div>

      {showEditModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>Edit Profile</h3>
            
            <label style={styles.modalLabel}>Bio</label>
            <textarea
              style={styles.modalTextArea}
              value={tempBio}
              onChange={(e) => setTempBio(e.target.value)}
              placeholder="Tell about yourself..."
              rows={4}
              maxLength={150}
            />
            <div style={styles.charCount}>{tempBio.length}/150</div>

            <div style={styles.modalButtons}>
              <button 
                style={styles.cancelBtn} 
                onClick={() => setShowEditModal(false)}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button 
                style={styles.saveBtn} 
                onClick={handleSaveBio}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      {selectedTrackForPreview && (
      <div style={styles.modalOverlay} onClick={closePreviewModal}>
        <div style={styles.previewModalContent} onClick={(e) => e.stopPropagation()}>
          
          {/* HEADER */}
          <div style={styles.previewModalHeader}>
            <div>
              <h3 style={styles.previewModalTitle}>{selectedTrackForPreview.title}</h3>
              <p style={styles.previewModalMeta}>
                {selectedTrackForPreview.createdAt?.toDate ? 
                  selectedTrackForPreview.createdAt.toDate().toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                  }) 
                  : 'Unknown date'
                }
              </p>
            </div>
            <button 
              style={styles.previewCloseButton}
              onClick={closePreviewModal}
            >
              ✕
            </button>
          </div>

          {/* CODE DISPLAY */}
          <div style={styles.previewCodeContainer}>
            <pre style={styles.previewCodeBlock}>
              {selectedTrackForPreview.code}
            </pre>
          </div>

          {/* FOOTER - PLAY IN STUDIO BUTTON */}
          <div style={styles.previewModalFooter}>
            <div style={styles.previewStats}>
              <span style={styles.previewStat}>▶ {selectedTrackForPreview.plays || 0} plays</span>
              <span style={styles.previewStat}>♥ {selectedTrackForPreview.likes || 0} likes</span>
              {selectedTrackForPreview.isPublic && (
                <span style={{...styles.previewStat, color: '#1DB954'}}>PUBLIC</span>
              )}
            </div>
            <button 
              style={styles.playInStudioButton}
              onClick={openInStudio}
            >
              ▶ Play in Studio
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

const styles = {
  pageWrapper: {
    backgroundColor: "#121212",
    minHeight: "100vh",
    color: "#fff",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  loadingContainer: {
    backgroundColor: "#121212",
    minHeight: "100vh",
    color: "#fff",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  loadingContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center", 
    alignItems: "center", 
    minHeight: "calc(100vh - 72px)",
    color: "#fff"
  },
  loadingSpinner: {
    width: "40px",
    height: "40px",
    border: "3px solid #333",
    borderTop: "3px solid #1DB954",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite"
  },
  mainContainer: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "40px 20px",
  },
  heroSection: {
    background: "linear-gradient(180deg, #282828 0%, #121212 100%)",
    padding: "30px",
    borderRadius: "16px",
    marginBottom: "30px",
    border: "1px solid #333",
    boxShadow: "0 10px 30px rgba(0,0,0,0.3)"
  },
  heroContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "30px"
  },
  heroLeftGroup: {
    display: "flex",
    alignItems: "center",
    gap: "25px",
    flex: 2,
    minWidth: "300px"
  },
  avatarLarge: {
    width: "100px",
    height: "100px",
    borderRadius: "50%",
    background: "#333",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "40px",
    fontWeight: "bold",
    position: "relative",
    boxShadow: "0 5px 15px rgba(0,0,0,0.5)",
    border: "2px solid #444"
  },
  levelBadge: {
    position: "absolute",
    bottom: "-5px",
    right: "-5px",
    background: "#1DB954",
    color: "#000",
    padding: "3px 8px",
    borderRadius: "10px",
    fontSize: "11px",
    fontWeight: "bold",
    border: "2px solid #121212"
  },
  userInfo: {
    flex: 1,
  },
  userHeaderRow: {
    display: "flex",
    alignItems: "center",
    gap: "15px",
    marginBottom: "5px"
  },
  userName: {
    fontSize: "26px",
    fontWeight: "700",
    margin: 0,
    letterSpacing: "-0.5px",
    color: "#fff"
  },
  editProfileBtn: {
    background: "transparent",
    border: "1px solid #666",
    color: "#ccc",
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    cursor: "pointer",
    transition: "all 0.2s"
  },
  rankBadge: {
    display: "inline-block",
    background: "rgba(255,255,255,0.08)",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: "600",
    color: "#aaa",
    marginBottom: "8px"
  },
  userBio: {
    fontSize: "14px",
    color: "#b3b3b3",
    maxWidth: "500px",
    lineHeight: "1.4",
    marginBottom: "8px"
  },
  joinDate: {
    fontSize: "12px",
    color: "#666",
  },
  xpCard: {
    flex: 1,
    minWidth: "280px",
    backgroundColor: "rgba(0,0,0,0.3)",
    padding: "20px",
    borderRadius: "12px",
    border: "1px solid #333"
  },
  xpHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px"
  },
  xpTitle: {
    fontSize: "14px",
    fontWeight: "bold",
    color: "#1DB954",
    letterSpacing: "1px"
  },
  xpPoints: {
    fontSize: "12px",
    color: "#888",
    fontFamily: "monospace"
  },
  xpProgressBarBg: {
    width: "100%",
    height: "8px",
    backgroundColor: "#444",
    borderRadius: "4px",
    marginBottom: "15px",
    overflow: "hidden"
  },
  xpProgressBarFill: {
    height: "100%",
    backgroundColor: "#1DB954",
    borderRadius: "4px",
    transition: "width 0.5s ease-in-out"
  },
  xpBreakdown: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "12px",
    marginBottom: "10px",
    color: "#ccc"
  },
  xpItem: {
    display: "flex",
    flexDirection: "column",
    gap: "2px"
  },
  xpGain: {
    fontSize: "10px",
    color: "#666"
  },
  nextLevelNote: {
    fontSize: "11px",
    color: "#555",
    textAlign: "center",
    fontStyle: "italic"
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "20px",
    marginBottom: "40px"
  },
  statCardHighlight: {
    background: "linear-gradient(135deg, rgba(255,87,34,0.15), rgba(255,87,34,0.05))",
    border: "1px solid rgba(255,87,34,0.3)",
    borderRadius: "12px",
    padding: "20px",
    display: "flex",
    alignItems: "center",
    gap: "15px"
  },
  statIconFire: { fontSize: "32px" },
  statInfo: { display: "flex", flexDirection: "column" },
  statLabel: { fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px", color: "#888", marginBottom: "5px" },
  statValueHighlight: { fontSize: "24px", fontWeight: "bold", color: "#FF5722" },
  statCard: {
    backgroundColor: "#181818",
    borderRadius: "12px",
    padding: "20px",
    border: "1px solid #282828",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center"
  },
  statValue: { fontSize: "28px", fontWeight: "700", color: "#fff", marginBottom: "4px" },
  sectionContainer: { marginBottom: "40px" },
  sectionTitle: { fontSize: "22px", fontWeight: "700", marginBottom: "20px", color: "#fff" },
  learningPathBar: { display: "flex", alignItems: "center", gap: "10px", overflowX: "auto", paddingBottom: "10px" },
  learningStepWrapper: { display: "flex", alignItems: "center", flex: 1 },
  learningStepCard: { backgroundColor: "#181818", border: "2px solid #333", borderRadius: "12px", padding: "15px", minWidth: "160px", width: "100%", transition: "all 0.3s" },
  stepHeader: { display: "flex", justifyContent: "space-between", marginBottom: "10px" },
  stepTitle: { fontWeight: "bold", fontSize: "14px", color: "#fff" },
  check: { color: "#1DB954", fontWeight: "bold" },
  progressBarBg: { height: "6px", background: "#333", borderRadius: "3px", marginBottom: "8px", overflow: "hidden" },
  progressBarFill: { height: "100%", background: "#1DB954", borderRadius: "3px" },
  stepMeta: { fontSize: "11px", color: "#888" },
  connectorLine: { flex: 1, height: "2px", background: "#333", minWidth: "20px", margin: "0 10px" },
  contentSection: { backgroundColor: "#181818", borderRadius: "16px", border: "1px solid #282828", overflow: "hidden" },
  tabsHeader: { display: "flex", borderBottom: "1px solid #282828", padding: "0 20px" },
  tab: { background: "transparent", border: "none", padding: "20px", fontSize: "14px", fontWeight: "600", color: "#888", cursor: "pointer", borderBottom: "3px solid transparent", transition: "color 0.2s" },
  tabActive: { background: "transparent", border: "none", padding: "20px", fontSize: "14px", fontWeight: "600", color: "#fff", cursor: "pointer", borderBottom: "3px solid #1DB954" },
  tabContent: { padding: "20px" },
  createNewRow: { display: "flex", alignItems: "center", gap: "15px", padding: "15px", border: "1px dashed #444", borderRadius: "8px", marginBottom: "20px", cursor: "pointer", color: "#aaa", transition: "all 0.2s" },
  plusIcon: { width: "24px", height: "24px", borderRadius: "50%", border: "1px solid #aaa", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", lineHeight: "1", paddingBottom: "2px" },
  trackList: { display: "flex", flexDirection: "column", gap: "8px" },
  trackRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderRadius: "8px", cursor: "pointer", transition: "background 0.2s" },
  trackLeft: { display: "flex", alignItems: "center", gap: "16px" },
  trackIcon: { width: "40px", height: "40px", background: "#282828", color: "#1DB954", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "4px", fontSize: "18px" },
  trackDetails: { display: "flex", flexDirection: "column" },
  trackTitle: { fontSize: "15px", fontWeight: "600", color: "#fff", marginBottom: "3px" },
  trackSubDetail: { fontSize: "12px", color: "#888" },
  trackRight: { display: "flex", alignItems: "center", gap: "20px" },
  metric: { fontSize: "13px", color: "#b3b3b3", minWidth: "60px", textAlign: "right" },
  badgePublic: { fontSize: "10px", background: "#1DB954", color: "#000", padding: "3px 8px", borderRadius: "4px", fontWeight: "bold" },
  badgePrivate: { fontSize: "10px", background: "#333", color: "#aaa", padding: "3px 8px", borderRadius: "4px", fontWeight: "bold" },
  emptyState: { textAlign: "center", padding: "40px", color: "#555", fontStyle: "italic" },

  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.7)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 2000,
    backdropFilter: "blur(5px)"
  },
  modalContent: {
    backgroundColor: "#1e1e1e",
    padding: "25px",
    borderRadius: "12px",
    width: "400px",
    maxWidth: "90%",
    border: "1px solid #333",
    boxShadow: "0 20px 50px rgba(0,0,0,0.5)"
  },
  modalTitle: {
    margin: "0 0 20px 0",
    fontSize: "20px",
    color: "#fff",
    textAlign: "center"
  },
  modalLabel: {
    display: "block",
    marginBottom: "8px",
    fontSize: "14px",
    color: "#aaa",
    fontWeight: "600"
  },
  modalTextArea: {
    width: "100%",
    backgroundColor: "#121212",
    border: "1px solid #333",
    borderRadius: "8px",
    color: "#fff",
    padding: "12px",
    fontSize: "14px",
    resize: "vertical",
    minHeight: "100px",
    fontFamily: "inherit",
    outline: "none",
    marginBottom: "5px",
    boxSizing: "border-box"
  },
  charCount: {
    textAlign: "right",
    fontSize: "12px",
    color: "#666",
    marginBottom: "20px"
  },
  modalButtons: {
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end"
  },
  cancelBtn: {
    padding: "8px 16px",
    backgroundColor: "transparent",
    border: "1px solid #444",
    color: "#ccc",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "13px"
  },
  saveBtn: {
    padding: "8px 20px",
    backgroundColor: "#1DB954",
    border: "none",
    color: "#000",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "600"
  },
  previewModalContent: {
  backgroundColor: "#1e1e1e",
  padding: "0",
  borderRadius: "16px",
  width: "700px",
  maxWidth: "90%",
  maxHeight: "80vh",
  border: "1px solid #333",
  boxShadow: "0 30px 80px rgba(0,0,0,0.8)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden"
},

previewModalHeader: {
  padding: "24px",
  borderBottom: "1px solid #2a2a2a",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: "#181818"
},

previewModalTitle: {
  margin: 0,
  fontSize: "22px",
  fontWeight: "700",
  color: "#fff"
},

previewModalMeta: {
  margin: "6px 0 0 0",
  fontSize: "13px",
  color: "#888"
},

previewCloseButton: {
  width: "36px",
  height: "36px",
  backgroundColor: "#2a2a2a",
  color: "#aaa",
  border: "none",
  borderRadius: "50%",
  cursor: "pointer",
  fontSize: "18px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.2s"
},

previewCodeContainer: {
  flex: 1,
  overflowY: "auto",
  padding: "24px",
  backgroundColor: "#0a0a0a"
},

previewCodeBlock: {
  margin: 0,
  padding: "20px",
  backgroundColor: "#000",
  color: "#e0e0e0",
  border: "1px solid #1f1f1f",
  borderRadius: "8px",
  fontSize: "13px",
  fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
  lineHeight: "1.6",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word"
},

previewModalFooter: {
  padding: "20px 24px",
  borderTop: "1px solid #2a2a2a",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: "#0f0f0f"
},

previewStats: {
  display: "flex",
  gap: "20px"
},

previewStat: {
  fontSize: "13px",
  color: "#888",
  fontWeight: "500"
},

playInStudioButton: {
  padding: "12px 28px",
  backgroundColor: "#1DB954",
  color: "#000",
  border: "none",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: "700",
  cursor: "pointer",
  transition: "all 0.2s",
  boxShadow: "0 4px 12px rgba(29, 185, 84, 0.3)"
}
};

export default Profile;